// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import React, { FC, ReactNode, useState } from "react";
// import StepFooter from "../StepFooter";
// import { LockIcon, ShieldCheckIcon } from "../icons";
// import { PanelProps } from "./types";
// import DocumentDropzone, { MAX_SIZE_MB } from "../../DocumentDropzone";
// import { Dictionary } from "../../i18n/strings";
// import { useLocale } from "../../i18n/locale";
//
// const getAssurances = (
//   t: Dictionary
// ): { title: string; caption: string; icon: ReactNode }[] => [
//   {
//     title: t.insurance.requiredTitle,
//     caption: t.insurance.requiredCaption,
//     icon: <ShieldCheckIcon className="h-6 w-6" />,
//   },
//   {
//     title: t.insurance.coversTitle,
//     caption: t.insurance.coversCaption,
//     icon: <ShieldCheckIcon className="h-6 w-6" />,
//   },
//   {
//     title: t.insurance.secureTitle,
//     caption: t.insurance.secureCaption,
//     icon: <LockIcon className="h-6 w-6" />,
//   },
// ];
//
// /** Step 6. */
// const InsurancePanel: FC<PanelProps> = ({ onBack, onContinue }) => {
//   const { t } = useLocale();
//   const [file, setFile] = useState<File | null>(null);
//   const assurances = getAssurances(t);
//
//   const handleSubmit = () => {
//     if (!file) return;
//     // TODO: upload the certificate to the DMS before advancing.
//     onContinue?.();
//   };
//
//   return (
//     <>
//       {/* Plain columns split by hairlines — the design has no cards here. */}
//       <ul className="grid grid-cols-3 border-y border-neutral-100 py-6">
//         {assurances.map(({ title, caption, icon }, i) => (
//           <li
//             key={title}
//             className={`flex flex-col items-center px-4 text-center ${
//               i > 0 ? "border-l border-neutral-200" : ""
//             }`}
//           >
//             <span className="text-primary-6000">{icon}</span>
//             <span className="mt-3 text-[12.5px] font-semibold leading-tight text-navy-900">
//               {title}
//             </span>
//             <span className="mt-1 text-[11px] leading-tight text-neutral-500">
//               {caption}
//             </span>
//           </li>
//         ))}
//       </ul>
//
//       <p className="mt-8 text-[13px] font-semibold text-navy-900">
//         {t.insurance.certificate}
//       </p>
//
//       <DocumentDropzone
//         id="workspace-insurance-certificate"
//         className="mt-3"
//         variant="wide"
//         prompt={t.desktop.insurancePanel.dropTitle}
//         browsePrefix={t.desktop.identityPanel.browsePrefix}
//         browseLabel={t.desktop.identityPanel.browse}
//         hint={t.desktop.identityPanel.formats(MAX_SIZE_MB)}
//         file={file}
//         onFileChange={setFile}
//       />
//
//       <StepFooter
//         onBack={onBack}
//         onContinue={handleSubmit}
//         label={t.desktop.steps.insurance.cta}
//         disabled={!file}
//       />
//     </>
//   );
// };
//
// export default InsurancePanel;

// ==== ACTIVE IMPLEMENTATION ==================================================

import React, { ChangeEvent, FC, useRef, useState } from "react";
import PanelHeading from "../PanelHeading";
import StepFooter from "../StepFooter";
import { useReservation } from "../reservationContext";
import { useOnboardingRecord } from "../onboardingContext";
import { submitInsurance } from "../onboardingConfig";
import { PanelProps } from "./types";
import { ACCEPTED_TYPES, MAX_SIZE_MB } from "../../DocumentDropzone";
import { useLocale } from "../../i18n/locale";
import { formatStayDate } from "../../i18n/formatDate";

/** The certificate is due this many days after moving in. */
const addDays = (isoDate: string, days: number) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * Step 6. Unlike the identity document this one has a deadline rather than a
 * gate — the tenant has already moved in — so the screen leads with the due
 * date and what the certificate has to show.
 *
 * The upload is a compact row, not a dropzone: by this point the tenant knows
 * the pattern, and the requirements above deserve the room.
 */
const InsurancePanel: FC<PanelProps> = ({
  state,
  update,
  onBack,
  onContinue,
}) => {
  const { locale, t } = useLocale();
  const reservation = useReservation();
  const onboarding = useOnboardingRecord();
  const c = t.desktop.insurancePanel;
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  /** A certificate filed on an earlier visit. */
  const alreadySent = onboarding?.insurance.file_url || "";

  /**
   * Send the certificate, then advance.
   *
   * Nothing moves until the DMS has the file. The step exists to collect it,
   * and a tenant who sailed past on a failed upload would believe they were
   * covered while the reservation held nothing.
   */
  const handleContinue = async () => {
    if (alreadySent || !state.insuranceFile || !onboarding) {
      onContinue?.();
      return;
    }

    setSending(true);
    setError("");
    const saved = await submitInsurance(
      onboarding.generated_contract_pdf_id,
      state.insuranceFile
    );
    setSending(false);

    if (!saved) {
      setError(t.dropzone.uploadFailed);
      return;
    }
    onContinue?.();
  };

  const dueDate = addDays(reservation.checkIn, reservation.insuranceDueDays);
  const dueDay = new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", {
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(`${dueDate}T00:00:00Z`));
  const dueMonth = new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", {
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${dueDate}T00:00:00Z`));
  const dueYear = dueDate.slice(0, 4);

  const requirements = [
    c.requirementName,
    c.requirementAddress,
    c.requirementDates,
    c.requirementLiability,
  ];

  // Same rules as DocumentDropzone, applied here because this step uses its own
  // compact control rather than the shared dropzone.
  const acceptFile = (e: ChangeEvent<HTMLInputElement>) => {
    const candidate = e.target.files?.[0];
    if (!candidate) return;

    if (!ACCEPTED_TYPES.includes(candidate.type)) {
      setError(t.dropzone.wrongType);
      update({ insuranceFile: null });
      return;
    }
    if (candidate.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(t.dropzone.tooLarge(MAX_SIZE_MB));
      update({ insuranceFile: null });
      return;
    }

    setError("");
    update({ insuranceFile: candidate });
  };

  const uploaded = Boolean(state.insuranceFile) || Boolean(alreadySent);

  // A file just chosen, else one filed on an earlier visit, else the prompt.
  const certificateName =
    state.insuranceFile?.name || onboarding?.insurance.file_name || c.uploadTitle;

  /**
   * How many whole days past the due date, or 0 while there is still time.
   *
   * Compared date to date rather than by timestamp: a certificate due today is
   * not late, and counting in hours would make it overdue from one minute past
   * midnight on the deadline itself.
   *
   * Once the certificate is in, the deadline stops mattering — a tenant who
   * filed late should not go on being told they were late.
   */
  const daysOverdue = (() => {
    if (uploaded) return 0;
    const today = new Date().toISOString().slice(0, 10);
    if (today <= dueDate) return 0;
    const gap = Date.parse(`${today}T00:00:00Z`) - Date.parse(`${dueDate}T00:00:00Z`);
    return Math.floor(gap / 86400000);
  })();
  const overdue = daysOverdue > 0;

  return (
    <>
      <PanelHeading eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} />

      {/* The date is stated the same way either side of the deadline; only its
          colour and the line beneath it change. The tenant is late, not locked
          out, so this reads as urgency rather than refusal. */}
      <div
        className={`mb-[18px] mt-5 flex items-center gap-[18px] border px-[19px] py-[14px] ${
          overdue ? "border-[#d8a49c] bg-[#fdf5f4]" : "border-line bg-[#fbfaf7]"
        }`}
      >
        <div
          className={`flex items-center gap-2 border-r pr-[18px] ${
            overdue ? "border-[#e6c5c0]" : "border-line"
          }`}
        >
          <strong
            className={`font-serif text-[42px] font-normal leading-none ${
              overdue ? "text-[#8c3a30]" : "text-navy"
            }`}
          >
            {dueDay}
          </strong>
          <span
            className={`text-[11px] min-[760px]:text-[9px] font-extrabold uppercase leading-[1.25] ${
              overdue ? "text-[#a8453a]" : "text-gold"
            }`}
          >
            {dueMonth}
            <br />
            {dueYear}
          </span>
        </div>

        <div>
          <small
            className={`block text-[11px] min-[760px]:text-[8px] font-extrabold uppercase tracking-[0.12em] ${
              overdue ? "text-[#a8453a]" : "text-gold"
            }`}
          >
            {overdue ? c.overdueLabel : c.dueLabel}
          </small>
          <strong
            className={`block text-[13px] font-bold ${
              overdue ? "text-[#8c3a30]" : "text-navy"
            }`}
          >
            {c.dueTitle}
          </strong>
          <p
            className={`m-0 text-[11px] min-[760px]:text-[10px] ${
              overdue ? "text-[#8c3a30]" : "text-[#92989c]"
            }`}
          >
            {overdue
              ? c.overdueBody(daysOverdue)
              : c.dueBody(formatStayDate(reservation.checkIn, locale))}
          </p>
        </div>
      </div>

      <div className="mb-[18px] border-l-[3px] border-navy bg-[#f3f5f6] px-[17px] py-[12px]">
        <strong className="font-serif text-[15px] font-normal text-navy">
          {c.requirementsTitle}
        </strong>
        <ul className="mt-2.5 grid list-disc grid-cols-1 gap-x-5 gap-y-1 pl-[17px] min-[760px]:grid-cols-2">
          {requirements.map((item) => (
            <li key={item} className="text-[11px] min-[760px]:text-[10.5px] text-[#69747d]">
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInput.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") fileInput.current?.click();
        }}
        className={`mb-[13px] flex cursor-pointer flex-wrap items-center gap-[14px] border-[1.5px] p-[22px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
          uploaded
            ? "border-solid border-[#8ca692] bg-[#f7faf7]"
            : "border-dashed border-[#c7c0b5] bg-[#fcfbf8] hover:border-gold"
        }`}
      >
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_TYPES.join(",")}
          hidden
          onChange={acceptFile}
        />

        <span
          className={`grid h-[43px] w-[43px] flex-none place-items-center rounded-full text-[20px] ${
            uploaded ? "bg-[#e3eee5] text-[#52745b]" : "bg-[#eee8de] text-gold"
          }`}
          aria-hidden="true"
        >
          {uploaded ? "✓" : "↑"}
        </span>

        <div className="min-w-0 flex-1">
          <strong className="block break-all text-[13px] font-bold text-navy">
            {certificateName}
          </strong>
          <small className="block text-[11px] min-[760px]:text-[10px] text-[#979ca0]">
            {uploaded
              ? c.uploadReady
              : t.desktop.identityPanel.formats(MAX_SIZE_MB)}
          </small>
        </div>

        <span className="flex-none border border-[#d5c9b8] bg-white px-[13px] py-2 text-[11px] min-[760px]:text-[10px] text-[#906b38]">
          {uploaded ? c.replace : c.browse}
        </span>
      </div>

      {error && (
        <p role="alert" className="mb-3 text-center text-xs text-red-600">
          {error}
        </p>
      )}

      <div className="mb-5 flex gap-2.5 text-[11px] min-[760px]:text-[10px] leading-[1.5] text-[#8b9298]">
        <span className="text-gold" aria-hidden="true">
          ◇
        </span>
        <p className="m-0 text-[11px] min-[760px]:text-[9px] font-medium">{c.note}</p>
      </div>

      <StepFooter
        onBack={onBack}
        backLabel={c.back}
        onContinue={handleContinue}
        label={c.cta}
        // Re-enabled: the step collects a certificate, so it cannot be walked
        // past without one. The server refuses an empty submission anyway.
        disabled={!uploaded}
        busy={sending}
      />
    </>
  );
};

export default InsurancePanel;
